import React, { useCallback, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
	Box,
	Divider,
	IconButton,
	InputAdornment,
	MenuItem,
	MenuItemProps,
	OutlinedInput
} from '@mui/material';
import Select from '../../../select/select';
import ChipOrPlaceholder from './chip-or-placeholder';
import Checkbox from '../../../checkbox/checkbox';
import Typography from '../../../typography/typography';
import Chip from '../../../chip/chip';
import { MoreIcon } from '../../../icons';
import './element.scss';
import { Add, Close } from '@mui/icons-material';
import { useTranslation } from "react-i18next";


interface DynamicSelectProps {
  formControl: any;
  fieldArrayName: string;
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: any;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  formType?: string;
  options?: Array<{ label: string; value: any }>;
  multiple?: boolean;
}

interface IMenuItems extends MenuItemProps {
	options: any[];
	multiple: boolean;
	placeholder: string;
	with_checkboxes: boolean;
	values: string | string[];
	field: any;
}

const generateMenuItems = ({
	options,
	multiple,
	placeholder,
	with_checkboxes,
	values,
	field
}: IMenuItems) => {
	const items: React.ReactElement[] = [];
	if (!multiple) {
		items.push(
			<MenuItem disabled value='' sx={{ opacity: `0 !important`, height: `0 !important`, p: `0 !important`, m: `0 !important` }}>
				<Typography type='s3' color='theme.secondary.1000'>
					<em>{placeholder}</em>
				</Typography>
			</MenuItem>
		);
	}
	options?.forEach((option, index) => {
		items.push(
			<MenuItem
				value={option.value}
				key={index}
				disabled={Boolean(option.disabled)}
				className={option?.isGroup ? 'select--MenuItemHeader' : ''}>
				<Box sx={{ display: 'flex', gap: 1 }}>
					{multiple && with_checkboxes && (
						<Checkbox
							checked={Boolean(values && values?.indexOf(option.value) > -1)}
						/>
					)}
					<Typography type='s3' color='theme.secondary.1000'>
						{option.label}
					</Typography>
				</Box>
			</MenuItem>
		);
		if (option?.isGroup && Array.isArray(option?.options)) {
			option.options.forEach((opt) => {
				items.push(
					<MenuItem
						value={opt.value}
						disabled={Boolean(opt.disabled)}
						sx={{ pl: 5 }}
						key={`${option.value}-${opt.value}`}>
						<Box sx={{ display: 'flex', gap: 1 }}>
							{multiple && with_checkboxes && (
								<Checkbox
									checked={Boolean(values && values?.indexOf(opt.value) > -1)}
								/>
							)}
							<Typography type='s3' color='theme.secondary.1000'>
								{opt.label}
							</Typography>
						</Box>
					</MenuItem>
				);
			});
		}
	});
	if (field.enable_footer) {
		items.push(
			<Box>
				<Divider flexItem />
				<MenuItem sx={{ gap: 1 }} onClick={field.onFooterClick}>
					<Add fontSize='small' />
					<Typography type='s4' color='theme.secondary.1000'>
						Create New {field.label}
					</Typography>
				</MenuItem>
			</Box>
		);
	}

	return items;
};
const SelectedItems = ({
	data,
	options
}: {
	data: string[];
	options: any[];
}) => {
	const displayTitle = data.map(
		(selectedItem) =>
			options?.find((option: any) => option.value === selectedItem)?.label ||
			selectedItem
	);
	const fDisplayTitle = displayTitle.filter(Boolean)
	return (
		<Typography
			type='s4'
			color='inherit'
			sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
			{fDisplayTitle.slice(0.4).join(', ')}
			{data.length > 4 && (
				<Chip variant='outlined' type='normal' label={<MoreIcon />} />
			)}
		</Typography>
	);
};

const DynamicSelect =  React.memo((props: DynamicSelectProps) => {
	const [options, setOptions] = useState([]);
	const [selectedValue, setSelectedValue] = useState(null);
	const { handleOptions } = props;
	useEffect(() => {
		if (handleOptions) {
			handleOptions(selectedValue);
		}
	}, [handleOptions, selectedValue]);
	const handleSearch = (search: string) => {
		if (props.options?.length && search) {
			const searchedOptions = props.options.filter((opt) =>
				React.isValidElement(opt.label)
					? opt?.searchLabel?.toLowerCase()?.includes(search.toLowerCase())
					: opt.label.toLowerCase().includes(search.toLowerCase())
			);
			setOptions(searchedOptions);
		} else {
			setOptions(props.options);
		}
	};

	const removeUndefine = (selectOptions: any[]) => {
		return selectOptions?.filter((option) => option);
	};
	const handleSetOptions = useCallback(() => {
		setOptions(props.options);
	}, [props.options]);

	useEffect(() => {
		handleSetOptions();
	}, [handleSetOptions]);
		const { t } = useTranslation();
	
	return (
		<Controller
			control={props.formControl}
			name={`${props.fieldArrayName}.${props.name}`}
			render={({ field, fieldState }) => (
				<>
					{props.placeholder === 'Select Currency'
						? setSelectedValue(field.value)
						: setSelectedValue(null)}
					<Select
						{...field}
						// onFocus={() => {
						// 	setOptions(props.options);
						// }}
						fullWidth
						dataPosition={props.position}
						dataName={`${props.fieldArrayName}.${props.name}`}
						placeholder={props.placeholder}
						label={props.label}
						value={
							field.value?.id || field.value?.label || field.value || (props.is_multiselect ? [] : '')
						}
						defaultValue={
							props.defaultValue || (props.is_multiselect ? [] : '')
						}
						required={props.formType === 'builder' ? false : props.required}
						size={props.size || 'small'}
						multiple={props.is_multiselect || false}
						disabled={props.disabled}
						variant='outlined'
						className={
							props.is_multiselect && removeUndefine(field.value)?.length
								? 'MultiSelect'
								: ''
						}
						renderValue={(selected: string | string[]) => (
							<>
								{Array.isArray(selected) ? (
									<ChipOrPlaceholder
										selectedLabel={props.selectedLabel}
										data={removeUndefine(selected)}
										placeholder={props.placeholder}
										onDelete={props?.onChipRemove}
										disabled={props.disabled}
									/>
								) : (
									options?.find(
										(option: any) =>
											(option.value?.id || option.value) === selected
									)?.label ||
									selected ||
									props.placeholder
								)}
							</>
						)}
						searchPlaceholder={`${t('common.search')} ${props.label}`}
						handleSearch={handleSearch}
						error={Boolean(fieldState.error?.message)}
						helperText={fieldState.error?.message || ''}
						onBlur={field.onBlur}
						input={
							!props?.is_multiselect && !props.disabled && field?.value ? (
								<OutlinedInput
									label=''
									endAdornment={
										<InputAdornment position='end'>
											{field.value && (
												<IconButton
													aria-label='clear selection'
													onClick={() => field.onChange(null)}
													edge='end'
													sx={{ mr: 1.5 }}
													size='small'>
													<Close sx={{ fontSize: '1rem' }} />
												</IconButton>
											)}
										</InputAdornment>
									}
								/>
							) : undefined
						}
						loading={props?.is_loading}>
						{generateMenuItems({
							options,
							multiple: Boolean(props.is_multiselect),
							placeholder: props.placeholder,
							values: field.value,
							with_checkboxes: true,
							field: props
						})}
					</Select>
					{props.is_multiselect && Array.isArray(field?.value) && (
						<SelectedItems data={field.value} options={options} />
					)}
				</>
			)}
		/>
	);
});
export { DynamicSelect };
export default DynamicSelect;
